import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import React from 'react';
import osvielLogo from '../../../assets/img/logos/osviel.jpg';
import {
    AUTORIZACION,
    AUTORIZACION_GIRO,
    COMPROMISO_PAGADORA,
    CONSTANCIA_CONSENTIMIENTO,
    MODIFICACION_CONDICIONES,
    OPERADORA,
    REGLAMENTOS,
    TERMINACION_ACUERDO,
    TERMINACION_CONTRATO,
    TITULO_ESPACIO_PAGADORA
} from './libranzaText';
import styles from './styles';

// Negritas (** **) y subrayados (__ __) del texto legal. Se separan al
// renderizar para no partir cada párrafo en fragmentos en libranzaText.
const MARCAS = /(\*\*[^*]+\*\*|__[^_]+__)/g;

const conMarcas = (texto) =>
    String(texto)
        .split(MARCAS)
        .filter(Boolean)
        .map((trozo, i) => {
            if (trozo.startsWith('**')) {
                return <Text key={i} style={styles.fuerte}>{trozo.slice(2, -2)}</Text>;
            }
            if (trozo.startsWith('__')) {
                return <Text key={i} style={styles.subrayado}>{trozo.slice(2, -2)}</Text>;
            }
            return trozo;
        });

const Parrafo = ({ children }) => <Text style={styles.parrafo}>{conMarcas(children)}</Text>;

// Dato del crédito dentro del párrafo corrido. Sin valor deja la línea de
// guiones del impreso, para diligenciarla a mano.
const Dato = ({ children, largo = 24 }) => (
    <Text style={styles.dato}>{children || '_'.repeat(largo)}</Text>
);

const Membrete = () => (
    <View style={styles.header}>
        <Image style={styles.logo} src={osvielLogo} />
        <View style={styles.membrete}>
            <Text style={styles.membreteFuerte}>{OPERADORA.lema}</Text>
            <Text style={styles.membreteFuerte}>{OPERADORA.nit}</Text>
            <Text style={styles.membreteLinea}>{OPERADORA.direccion}</Text>
            <Text style={styles.membreteLinea}>{OPERADORA.telefonos}</Text>
            <Text style={styles.membreteLinea}>{OPERADORA.ciudad}</Text>
        </View>
    </View>
);

const CampoFirma = ({ etiqueta, valor }) => (
    <View style={styles.firmaCampo}>
        <Text style={styles.firmaEtiqueta}>{etiqueta}</Text>
        <Text style={styles.firmaValor}>{valor || ' '}</Text>
    </View>
);

// El impreso trae dos bloques idénticos, deudor y deudor solidario. El sistema
// no maneja codeudores, así que el segundo sale siempre en blanco.
const BloqueFirma = ({ titulo, datos }) => (
    <View style={styles.firmaCol}>
        <View style={styles.firmaDatos}>
            <View style={styles.firmaLinea}>
                <Text style={styles.firmaRol}>{titulo}</Text>
            </View>
            <CampoFirma etiqueta="NOMBRE:" valor={datos?.nombre} />
            <CampoFirma etiqueta="C.C.:" valor={datos?.identificacion} />
            <CampoFirma etiqueta="DIRECCIÓN:" valor={datos?.direccion} />
            <CampoFirma etiqueta="TELÉFONO:" valor={datos?.telefono} />
            <CampoFirma etiqueta="E-MAIL:" valor={datos?.email} />
        </View>
        <View style={styles.huella} />
    </View>
);

const EspacioPagadora = () => (
    <>
        <View style={styles.bannerPagadora}>
            <Text style={styles.bannerTexto}>{TITULO_ESPACIO_PAGADORA}</Text>
        </View>
        <Parrafo>{COMPROMISO_PAGADORA}</Parrafo>
        <View style={styles.funcionarioRow}>
            <View style={styles.funcionarioCol}>
                <View style={styles.firmaLinea}>
                    <Text style={styles.firmaRol}>
                        Firma del funcionario autorizado por la Entidad Pagadora.
                    </Text>
                </View>
                <CampoFirma etiqueta="NOMBRE:" />
                <CampoFirma etiqueta="C.C.:" />
                <CampoFirma etiqueta="CARGO:" />
                <CampoFirma etiqueta="E-MAIL:" />
            </View>
            <View style={styles.selloCol}>
                <View style={styles.sello} />
                <Text style={styles.selloTexto}>SELLO DE LA ENTIDAD PAGADORA</Text>
            </View>
        </View>
    </>
);

const LibranzaFormato = ({ data }) => {
    if (!data) return null;

    const { deudor = {}, numero, fecha, montoTransado, valorCuota, cuotas, entidadPagadora, concepto } = data;

    return (
        <Document title={`Libranza ${numero || ''}`}>
            <Page size="A4" style={styles.page}>
                <Membrete />

                <View style={styles.numeroRow}>
                    <Text style={styles.numeroTitulo}>LIBRANZA N°</Text>
                    <Text style={styles.numeroValor}>{numero || ' '}</Text>
                </View>

                <View style={styles.encabezadoDatos}>
                    <Text style={styles.etiqueta}>Monto Transado $</Text>
                    <Text style={[styles.lineaValor, styles.dato, { width: 190 }]}>
                        {montoTransado || ' '}
                    </Text>
                    <Text style={[styles.etiqueta, { marginLeft: 22 }]}>Fecha:</Text>
                    <Text style={[styles.lineaValor, styles.dato, { flex: 1 }]}>{fecha || ' '}</Text>
                </View>

                <Text style={styles.parrafo}>
                    {'Yo, '}
                    <Dato largo={60}>{deudor.nombre}</Dato>
                    {conMarcas(AUTORIZACION.mayorEdad)}
                    <Dato largo={26}>{deudor.ciudad}</Dato>
                    {conMarcas(AUTORIZACION.identificado)}
                    <Dato largo={22}>{deudor.identificacion}</Dato>
                    {' de '}
                    <Dato largo={20}>{deudor.lugarExpedicion}</Dato>
                    {conMarcas(AUTORIZACION.autorizo)}
                    <Dato largo={34}>{entidadPagadora}</Dato>
                    {conMarcas(AUTORIZACION.marcoLegal)}
                    <Dato largo={28}>{concepto}</Dato>
                    {conMarcas(AUTORIZACION.retencion)}
                    <Dato largo={16}>{valorCuota}</Dato>
                    {conMarcas(AUTORIZACION.durante)}
                    <Dato largo={6}>{cuotas}</Dato>
                    {conMarcas(AUTORIZACION.cierre)}
                </Text>

                <Parrafo>{AUTORIZACION_GIRO}</Parrafo>
                <Parrafo>{MODIFICACION_CONDICIONES}</Parrafo>
                <Parrafo>{TERMINACION_CONTRATO}</Parrafo>
                <Parrafo>{TERMINACION_ACUERDO}</Parrafo>
                <Parrafo>{REGLAMENTOS}</Parrafo>
                <Parrafo>{CONSTANCIA_CONSENTIMIENTO}</Parrafo>

                <Text style={styles.atentamente}>Atentamente;</Text>

                <View style={styles.firmasRow}>
                    <BloqueFirma titulo="FIRMA DEL DEUDOR" datos={deudor} />
                    <BloqueFirma titulo="FIRMA DEL DEUDOR SOLIDARIO" />
                </View>

                <EspacioPagadora />
            </Page>
        </Document>
    );
};

export default LibranzaFormato;
